'use client'

import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogBackdrop,
    DialogPanel,
    TransitionChild
} from '@headlessui/react'
import { Link } from 'react-router-dom';
import {
    XMarkIcon,
} from '@heroicons/react/24/outline'
import { Path } from '../utils/path';
import IconButton from '../components/buttons/IconBtn';
import { toggleDarkMode, initializeDarkMode } from '../services/dark-mode.service';
import Logo from '../components/Logo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

interface NavItem {
    to?: string
    onClick?: () => void
    label: string
    skipHeader?: boolean
    active?: boolean
}

const Header = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false)
    const [isDark, setIsDark] = useState<boolean>(false)
    const { t } = useTranslation();

    let NAV_ITEMS: NavItem[] = [];
    if (process.env.REACT_APP_SHOW_NAV) {
        NAV_ITEMS = [
            { to: Path.HOME, label: "ui.home" },
            { to: Path.ABOUT, label: "ui.about" },
            { to: Path.PORTFOLIO, label: "ui.portfolio" },
        ];
    }

    const handleToggleDarkMode = () => {
        toggleDarkMode();
        setIsDark(document.documentElement.classList.contains('dark'));
    };

    useEffect(() => {
        initializeDarkMode();
        setIsDark(document.documentElement.classList.contains('dark'));
    }, []);

    return (
        <header className="primary-bg header">
            <nav aria-label="Global" className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
                <div className="flex lg:flex-1">
                    <Link to={Path.HOME}>
                        <Logo showName></Logo>
                    </Link>
                </div>

                <div className="hidden lg:flex lg:gap-x-12">
                    {NAV_ITEMS
                        .filter(item => !item.skipHeader)
                        .map((item, idx) => {
                            const key = `${item.to || ''}-${item.label}-${idx}`;
                            if (item.to) {
                                return (
                                    <div className='flex gap-1' key={key}>
                                        <Link
                                            to={item.to}
                                            className={`nav-item text-sm/6 font-semibold flex items-center gap-2${item.active ? ' active' : ''}`}
                                        >
                                            {t(item.label)}
                                        </Link>
                                    </div>
                                )
                            }
                            if (item.onClick) {
                                return (
                                    <button
                                        key={key}
                                        onClick={item.onClick}
                                        className="text-sm/6 font-semibold primary-text bg-transparent border-none p-0 cursor-pointer flex items-center gap-2"
                                    >
                                        {t(item.label)}
                                    </button>
                                )
                            }
                            return ''
                        })}
                </div>
                <div className="flex lg:hidden">
                    <button
                        type="button"
                        onClick={() => setMobileMenuOpen(true)}
                        className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 primary-text"
                    >
                        <span className="sr-only">Open main menu</span>
                        <svg className="size-8" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"></path>
                        </svg>
                    </button>

                </div>

                <div className="hidden lg:flex lg:flex-1 lg:justify-end">
                    <div className="flex items-center gap-6">
                        <LanguageSwitcher />
                        <IconButton onClick={handleToggleDarkMode}>
                            {isDark ? (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
                                </svg>
                            )}
                        </IconButton>
                    </div>
                </div>

            </nav>

            <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="relative lg:hidden">
                <TransitionChild
                    enter="transition-opacity ease-linear duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity ease-linear duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <DialogBackdrop
                        className="fixed inset-0 bg-gray-500/75"
                    />
                </TransitionChild>
                <div className="fixed inset-0 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                            <TransitionChild
                                enter="transform transition ease-in-out duration-300"
                                enterFrom="translate-x-full"
                                enterTo="translate-x-0"
                                leave="transform transition ease-in-out duration-300"
                                leaveFrom="translate-x-0"
                                leaveTo="translate-x-full"
                            >
                                <DialogPanel
                                    transition
                                    className="pointer-events-auto relative w-screen max-w-md transform transition duration-300 ease-in-out data-closed:translate-x-full sm:duration-300"
                                >
                                    <div className="flex h-full flex-col primary-bg shadow-xl">
                                        <div className="relative mt-6 flex-1 px-4 sm:px-6">
                                            <div className="flex items-center justify-between">
                                                <Link to={Path.HOME}>
                                                    <Logo showName></Logo>
                                                </Link>
                                                <button
                                                    type="button"
                                                    onClick={() => setMobileMenuOpen(false)}
                                                    className="-m-2.5 rounded-md p-2.5 primary-text">
                                                    <span className="sr-only">Zamknij menu</span>
                                                    <XMarkIcon aria-hidden="true" className="size-6" />
                                                </button>
                                            </div>

                                            <div className="mt-6 flow-root">
                                                <div className="-my-6 divide-y divide-gray-500/10">
                                                    <div className="py-6">
                                                        {NAV_ITEMS
                                                            .map((item, idx) => {
                                                                const key = `${item.to || ''}-${item.label}-${idx}`;
                                                                if (item.to) {
                                                                    return (
                                                                        <Link
                                                                            key={key}
                                                                            to={item.to}
                                                                            onClick={() => setMobileMenuOpen(false)}
                                                                            className={`flex justify-between gap-x-6 py-3 cursor-pointer secondary-bg rounded-lg shadow mb-2 px-4 items-center font-semibold primary-text${item.active ? ' active' : ''}`}
                                                                        >
                                                                            <span className="flex items-center gap-2">
                                                                                {t(item.label)}
                                                                            </span>
                                                                        </Link>
                                                                    )
                                                                }
                                                                if (item.onClick) {
                                                                    return (
                                                                        <button
                                                                            key={key}
                                                                            onClick={() => {
                                                                                item.onClick?.();
                                                                                setMobileMenuOpen(false);
                                                                            }}
                                                                            className="flex w-full justify-between gap-x-6 py-3 cursor-pointer secondary-bg rounded-lg shadow mb-2 px-4 items-center font-semibold primary-text"
                                                                        >
                                                                            <span className="flex items-center gap-2">
                                                                                {t(item.label)}
                                                                            </span>
                                                                        </button>
                                                                    )
                                                                }
                                                                return ''
                                                            })}

                                                        <div className="mt-4 pt-4 border-t border-gray-300 dark:border-gray-600">
                                                            <div className="flex flex-col gap-3">
                                                                <div className="flex justify-between items-center py-3 px-4 secondary-bg rounded-lg shadow">
                                                                    <span className="font-semibold primary-text">Język / Language</span>
                                                                    <LanguageSwitcher />
                                                                </div>

                                                                <button
                                                                    onClick={handleToggleDarkMode}
                                                                    className="flex justify-between items-center py-3 px-4 secondary-bg rounded-lg shadow"
                                                                >
                                                                    <span className="font-semibold primary-text">
                                                                        {isDark ? t('ui.lightMode') : t('ui.darkMode')}
                                                                    </span>
                                                                    {isDark ? (
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 primary-text" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                                                                        </svg>
                                                                    ) : (
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 primary-text" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
                                                                        </svg>
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </div>
            </Dialog>

        </header>
    )
}

export default Header;
